import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import REFERRAL_CODES from '../constants/referralCodes';

const STORAGE_KEY = 'labourando:referral';

/**
 * Reads the ?r= param from the URL, resolves it via REFERRAL_CODES,
 * persists it for the session, and registers it as PostHog super properties.
 *
 * Also sets $initial_referral_code on first touch (person property).
 */
export default function useReferralTracking() {
  const location = useLocation();
  const posthog = usePostHog();
  const registered = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('r');

    if (code) {
      const meta = REFERRAL_CODES[code];
      const data = meta
        ? { referral_code: code, ...meta }
        : { referral_code: code, source: code, medium: 'unknown', campaign: 'unknown' };

      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch {
        // private browsing — best effort
      }
    }

    // Always try to hydrate from session storage (covers page refreshes)
    let stored = null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) stored = JSON.parse(raw);
    } catch {
      // ignore
    }

    if (stored && posthog && !registered.current) {
      registered.current = true;

      // Super properties — attached to every future event in this session
      posthog.register({
        referral_code: stored.referral_code,
        referral_source: stored.source,
        referral_medium: stored.medium,
        referral_campaign: stored.campaign,
      });

      // Person property (first-touch attribution, set once)
      posthog.setPersonPropertiesForFlags({
        initial_referral_code: stored.referral_code,
      });
      posthog.people?.set_once?.({
        initial_referral_code: stored.referral_code,
        initial_referral_source: stored.source,
      });
    }
  }, [location.search, posthog]);
}
